import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  IconButtonProps,
  Skeleton,
  Stack,
  SxProps,
  Typography,
  styled,
} from '@mui/material';
import {Moment} from 'moment';
import {ExpandMore} from '@mui/icons-material';
import {EntityShift} from '../../backend/interfaces/entity-shift';
import {titleCase} from '../../utils/functions';
import {avatarSizesAndCoefficients, shiftsBackgroundColors} from '../../utils/constants';
import {EntityShiftsAllocationsRepository} from '../../repositories/entity-shifts-allocations';
import {UsersRepository} from '../../repositories/users';
import {EntityShiftAllocation} from '../../backend/interfaces/entity-shift-allocation';
import {translations} from '../../i18n/translations';
import {UserPublicInfo} from '../../backend/responses/user-public-info';
import {AuthContextType} from '../../contexts/auth/types';
import {AuthContext} from '../../contexts/auth/context';

const collapsibleSx: SxProps = {
  width: '100%',
  borderBottom: `2px solid ${shiftsBackgroundColors.green.light}`,
};

const headerSx: SxProps = {
  flexDirection: 'row',
  alignItems: 'center',
  userSelect: 'none',
  padding: '12px 8px',
};

const lightTextSx: SxProps = {
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '20px',
  color: 'var(--dcm-gray-input)',
};

const regularTextSx: SxProps = {
  fontSize: '16px',
  lineHeight: '20px',
  color: 'var(--dcm-gray)',
};

const caregiversSx: SxProps = {
  padding: '0 8px 4px',
};

const darkTextSx: SxProps = {
  ...regularTextSx,
  fontWeight: 500,
};

const avatarSx: SxProps = {
  width: avatarSizesAndCoefficients.avatarSizes.small,
  height: avatarSizesAndCoefficients.avatarSizes.small,
};

interface ExpandMoreProps extends IconButtonProps {
  expanded: boolean;
}

const ExpandMoreStyled = styled((props: ExpandMoreProps) => {
  const {expanded, ...other} = props;
  return <IconButton {...other} />;
})(({theme, expanded}) => ({
  transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface CollapsibleShiftInfoProps {
  shift: EntityShift;
  date: Moment;
  startDate: Moment;
  endDate: Moment;
  allocations?: EntityShiftAllocation[];
  sx?: SxProps;
}

const CollapsibleShiftInfo = ({
  shift,
  date,
  startDate,
  endDate,
  allocations: shiftAllocations,
  sx,
}: CollapsibleShiftInfoProps) => {
  const {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  const authContext: AuthContextType = useContext(AuthContext);
  const caregiverId = authContext.state.session?.id;
  const [expanded, setExpanded] = useState(false);
  const [allocations, setAllocations] = useState<EntityShiftAllocation[] | undefined>(
    shiftAllocations
  );
  const [otherCaregivers, setOtherCaregivers] = useState<UserPublicInfo[] | undefined>(undefined);
  const handleExpandClick = () => setExpanded((prevExpanded) => !prevExpanded);

  const dateStr = useMemo(() => {
    // Same day, only show one date
    if (startDate.isSame(endDate, 'day')) {
      return startDate.format('L');
    }
    // Different days, show both
    return `${startDate.format('L')} - ${endDate.format('L')}`;
  }, [startDate, endDate]);

  const fetchAllocations = useCallback(async () => {
    const response =
      await EntityShiftsAllocationsRepository.instance.getListFormatEntityShiftsAllocations(
        entityId,
        date.startOf('day').toISOString(),
        date.endOf('day').toISOString(),
        shift.id
      );

    setAllocations(response);
  }, [entityId, date, shift.id]);

  const fetchCaregivers = useCallback(async () => {
    const users = await Promise.all(
      allocations!
        .reduce((uniqueAllocations, allocation) => {
          if (
            // Filter out allocations for the current caregiver, as we only want to display other caregivers
            allocation.userId !== caregiverId &&
            // Filter out duplicates
            !uniqueAllocations.some(
              (uniqueAllocation) => uniqueAllocation.userId === allocation.userId
            )
          ) {
            uniqueAllocations.push(allocation);
          }

          return uniqueAllocations;
        }, [] as EntityShiftAllocation[])
        .map((allocation) => UsersRepository.instance.getInfoById(allocation.userId))
    );

    setOtherCaregivers(users);
  }, [caregiverId, allocations]);

  useEffect(() => {
    // Only fetch allocations if none were injected as props, and if the component has been expanded
    if (expanded && allocations === undefined) {
      fetchAllocations();
    }
  }, [expanded, allocations, fetchAllocations]);

  useEffect(() => {
    // Only fetch caregivers if the component is expanded and allocations were fetched already
    // (or were injected via props)
    if (expanded && allocations && otherCaregivers === undefined) {
      // If there are no allocations to a given shift, skip fetching the respective users
      if (allocations.length > 0) {
        fetchCaregivers();
      } else {
        setOtherCaregivers([]);
      }
    }
  }, [expanded, allocations, otherCaregivers, fetchCaregivers]);

  const LoadingOtherShiftMembers = () => (
    <>
      {new Array(3).fill(1).map((_item, index) => (
        <Stack direction="row" alignItems="center" spacing={1} key={`skeleton_${index}`}>
          <Skeleton variant="circular" sx={avatarSx} />
          <Skeleton width={100} sx={{fontSize: '16px'}} />
        </Stack>
      ))}
    </>
  );

  const NoOtherShiftMembers = () => (
    <Typography sx={regularTextSx}>
      {t(translations.daily_care_log.shift_collapsible.no_other_shift_members)}
    </Typography>
  );

  const OtherCaregiversList = () => (
    <>
      <Typography sx={regularTextSx}>
        {t(translations.daily_care_log.shift_collapsible.other_shift_members)}
      </Typography>
      {otherCaregivers!.map((caregiver) => (
        <Stack direction="row" alignItems="center" spacing={1} key={caregiver.id}>
          <Avatar src={caregiver.picture ?? ''} sx={avatarSx} />
          <Typography sx={regularTextSx}>{caregiver.name}</Typography>
        </Stack>
      ))}
    </>
  );

  return (
    <Box sx={{...collapsibleSx, ...sx}}>
      <Stack sx={headerSx} onClick={handleExpandClick}>
        <Box flexGrow={1}>
          <Typography sx={lightTextSx}>
            {t(translations.daily_care_log.shift_collapsible.shift)}
          </Typography>
          <Typography mt={1}>
            {(shift.abbreviation || shift.name) && (
              <Typography component="span" sx={darkTextSx}>
                {shift.abbreviation ? `${shift.abbreviation} - ` : ''}
                {titleCase(shift.name)} -{' '}
              </Typography>
            )}
            <Typography component="span" sx={lightTextSx}>
              {dateStr}
            </Typography>
          </Typography>
        </Box>
        <Box flexShrink={0}>
          <ExpandMoreStyled expanded={expanded} aria-expanded={expanded}>
            <ExpandMore htmlColor="var(--dcm-gray)" />
          </ExpandMoreStyled>
        </Box>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={caregiversSx}>
          <Stack spacing={1.5}>
            {otherCaregivers === undefined ? (
              <LoadingOtherShiftMembers />
            ) : otherCaregivers.length === 0 ? (
              <NoOtherShiftMembers />
            ) : (
              <OtherCaregiversList />
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleShiftInfo;
